import { useDebouncedValue } from "@mantine/hooks";
import { useMemo, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { useHeldEmails, useHeldEmailStats } from "./useSystemEmails";
import { HeldEmailState } from "../../services/admin/systemEmails.service";

export type PendingHeldEmailAction = {
  type: "release" | "cancel";
  ids: string[];
} | null;
export interface HeldEmailQueueState {
  recipient: string;
  states: HeldEmailState[];
  page: number;
  selectedIds: Set<string>;
  detailId: string | null;
  pendingAction: PendingHeldEmailAction;
}
export type HeldEmailQueueAction =
  | { type: "recipient"; value: string }
  | { type: "states"; value: HeldEmailState[] }
  | { type: "page"; value: number }
  | { type: "selection"; value: Set<string> }
  | { type: "detail"; value: string | null }
  | { type: "action"; value: PendingHeldEmailAction };

function reducer(
  state: HeldEmailQueueState,
  action: HeldEmailQueueAction
): HeldEmailQueueState {
  if (action.type === "recipient")
    return { ...state, recipient: action.value, page: 1 };
  if (action.type === "states")
    return { ...state, states: action.value, page: 1, selectedIds: new Set() };
  if (action.type === "page") return { ...state, page: action.value };
  if (action.type === "selection")
    return { ...state, selectedIds: action.value };
  if (action.type === "detail") return { ...state, detailId: action.value };
  return { ...state, pendingAction: action.value };
}

export function useHeldEmailQueueState() {
  const [searchParams] = useSearchParams();
  const initial = useMemo<HeldEmailQueueState>(
    () => ({
      recipient: "",
      states: ["pending", "blocked", "failed"],
      page: 1,
      selectedIds: new Set(),
      detailId: searchParams.get("heldEmailId"),
      pendingAction: null,
    }),
    [searchParams]
  );
  return useReducer(reducer, initial);
}

export function useHeldEmailQueueData(state: HeldEmailQueueState) {
  const [recipient] = useDebouncedValue(state.recipient.trim(), 250);
  const emails = useHeldEmails({
    states: state.states,
    recipient: recipient || undefined,
    page: state.page,
    limit: 50,
  });
  const stats = useHeldEmailStats();
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((emails.data?.total ?? 0) / 50)),
    [emails.data?.total]
  );
  const filtered = useMemo(
    () => Boolean(recipient || state.states.length),
    [recipient, state.states.length]
  );
  return { emails, stats, totalPages, filtered };
}
